import { cn } from '@/lib/utils'
import type { CodeLabel } from '@/shared/api/types'

/**
 * Outcome and category chips.
 *
 * Colour never carries the meaning alone — every chip shows its label too, so a
 * red/green distinction is a reinforcement rather than the signal.
 *
 * The backend sends `{code, displayName}`, so the label is the server's. Only the
 * colour is decided here, and an unrecognised code falls through to neutral
 * rather than throwing — a newer backend must render, not crash.
 */

const OUTCOME_STYLE: Record<string, string> = {
  SUCCESS: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FAILURE: 'bg-red-50 text-red-700 border-red-200',
  DENIED: 'bg-amber-50 text-amber-700 border-amber-200',
}

const OUTCOME_DOT: Record<string, string> = {
  SUCCESS: 'bg-emerald-500',
  FAILURE: 'bg-red-500',
  DENIED: 'bg-amber-500',
}

export function OutcomeBadge({ outcome }: { outcome?: CodeLabel | null }) {
  if (!outcome) return <span className="text-muted-foreground">—</span>
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md whitespace-nowrap',
      OUTCOME_STYLE[outcome.code] ?? 'bg-muted text-muted-foreground border-border',
    )}>
      <span className={cn('erp-status-dot', OUTCOME_DOT[outcome.code] ?? 'bg-slate-400')} />
      {outcome.displayName || outcome.code}
    </span>
  )
}

const CATEGORY_STYLE: Record<string, string> = {
  SECURITY: 'bg-red-50 text-red-700 border-red-200',
  DATA: 'bg-sky-50 text-sky-700 border-sky-200',
  CONFIGURATION: 'bg-amber-50 text-amber-700 border-amber-200',
  ACCESS: 'bg-violet-50 text-violet-700 border-violet-200',
  SYSTEM: 'bg-slate-100 text-slate-600 border-slate-200',
}

export function CategoryBadge({ category }: { category?: CodeLabel | null }) {
  if (!category) return <span className="text-muted-foreground">—</span>
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 text-erp-xs font-medium border rounded-md whitespace-nowrap',
      CATEGORY_STYLE[category.code] ?? 'bg-muted text-muted-foreground border-border',
    )}>
      {category.displayName || category.code}
    </span>
  )
}
